import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { useToast } from "@/components/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { MenuItem, MenuSeparator } from "@/components/ui/menu";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  detachStickyRow,
  ManagedStickyRow,
  setStickyRowOptOut,
  stickySetsForFeedQueryOptions,
} from "@/lib/api";

/**
 * A feed's view of a post that a sticky set put there.
 *
 * The per-feed panel lists sticky posts by `sticky_post_id`, and a set-managed
 * row is an ordinary row in that same list -- so these look up the row by
 * (post, type) and offer the three ways a feed can overrule its set.
 */
function useManagedRow(
  feedId: number,
  stickyPostId: number,
  stickyType?: string,
): ManagedStickyRow | undefined {
  const { data } = useQuery(stickySetsForFeedQueryOptions(feedId));
  return data?.find(
    (row) =>
      row.sticky_post_id === stickyPostId &&
      (stickyType === undefined || row.sticky_type === stickyType),
  );
}

export function StickySetBadge({
  feedId,
  stickyPostId,
  stickyType,
}: {
  feedId: number;
  stickyPostId: number;
  stickyType?: string;
}) {
  const managed = useManagedRow(feedId, stickyPostId, stickyType);

  if (!managed) {
    return (
      <SimpleTooltip message="Added on this feed only.">
        <span className="text-xs text-slate-dim">This feed</span>
      </SimpleTooltip>
    );
  }

  if (managed.opted_out) {
    return (
      <SimpleTooltip
        message={`Turned off on this feed. “${managed.sticky_set_name}” won't switch it back on.`}
      >
        <Badge variant="secondary">Off here</Badge>
      </SimpleTooltip>
    );
  }

  return (
    <SimpleTooltip
      message={`Managed by the sticky set “${managed.sticky_set_name}”. Edit it there to change it everywhere.`}
    >
      <Link
        to="/app/sticky-sets/$setId/view"
        params={{ setId: `${managed.sticky_set_id}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <Badge variant="outline" className="max-w-full hover:underline">
          {/* A set name is free text and the column is narrow, so clip it to one
              line rather than letting it wrap and heighten every row. The full
              name is in the tooltip either way. */}
          <span className="truncate">{managed.sticky_set_name}</span>
        </Badge>
      </Link>
    </SimpleTooltip>
  );
}

/**
 * The three escapes, which are genuinely different and easy to conflate:
 *
 *   Turn off here  -- keep the link, stop showing it. Survives reconcile
 *                     because it sets the opt-out flag.
 *   Take a copy    -- detach the row so this feed owns it and can edit it
 *                     freely. The set then re-creates its own copy.
 *   Remove feed    -- drop this feed from the set entirely, so ALL of that
 *                     set's posts leave.
 */
export function StickySetMenuItems({
  feedId,
  stickyPostId,
  stickyType,
}: {
  feedId: number;
  stickyPostId: number;
  stickyType?: string;
}) {
  const { toast } = useToast();
  const managed = useManagedRow(feedId, stickyPostId, stickyType);

  const optOut = useMutation({
    mutationFn: (optedOut: boolean) =>
      setStickyRowOptOut(managed!.association_id, optedOut),
    onSuccess: (_data, optedOut) =>
      toast({
        title: optedOut ? "Turned off on this feed" : "Turned back on",
        description: optedOut
          ? `“${managed?.sticky_set_name}” will leave it off here.`
          : "It follows the set again.",
      }),
    onError: (error) =>
      toast({
        title: "Couldn't change that",
        description: error.message,
        variant: "destructive",
      }),
  });

  const detach = useMutation({
    mutationFn: () => detachStickyRow(managed!.association_id),
    onSuccess: () =>
      toast({
        title: "Copy taken",
        description:
          "This feed now owns this post and can edit it. The set will add its own copy back.",
      }),
    onError: (error) =>
      toast({
        title: "Couldn't detach that",
        description: error.message,
        variant: "destructive",
      }),
  });

  if (!managed) return null;

  return (
    <>
      <MenuSeparator />
      <MenuItem
        disabled={optOut.isPending}
        onSelect={() => optOut.mutate(!managed.opted_out)}
      >
        {managed.opted_out ? "Turn back on here" : "Turn off on this feed"}
      </MenuItem>
      <MenuItem disabled={detach.isPending} onSelect={() => detach.mutate()}>
        Take a copy this feed owns
      </MenuItem>
      <MenuItem asChild>
        <Link
          to="/app/sticky-sets/$setId/view"
          params={{ setId: `${managed.sticky_set_id}` }}
        >
          Edit &ldquo;{managed.sticky_set_name}&rdquo;
        </Link>
      </MenuItem>
    </>
  );
}
